/**
 * Inspector Bottom Sheet Component
 *
 * Mobile-friendly bottom sheet for algebra/observables inspection.
 * Shows as a slide-up panel with tabs for Algebra, Observables, Status.
 *
 * @see docs/designs/smart-asset-algebra-visualization.md#11-compact--mobile-layout
 * @see docs/discoveries/2026-01-05-ui-schema-consumption.md
 */
import { memo, useState, useCallback } from "react";
import type { ValueFlowsSchema } from "../../../types/valueFlows";
import { SchemaStatusHeader } from "./SchemaStatusHeader";
import { AlgebraInspectorPanel } from "./AlgebraInspectorPanel";
import { ObservablesInspectorPanel } from "./ObservablesInspectorPanel";

interface InspectorBottomSheetProps {
  /** Value flows schema */
  schema: ValueFlowsSchema;
  /** Whether the sheet is open */
  isOpen: boolean;
  /** Callback when sheet is closed */
  onClose: () => void;
  /** Additional CSS classes */
  className?: string;
}

type TabId = "algebra" | "observables" | "status";

interface Tab {
  id: TabId;
  label: string;
  count?: number;
}

/**
 * Close icon SVG
 */
const CloseIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

/**
 * Drag handle for sheet
 */
const DragHandle = () => (
  <div className="flex justify-center py-2">
    <div className="w-10 h-1 bg-sa-text-dim rounded-full" />
  </div>
);

/**
 * Tab button component
 */
const TabButton = memo(
  ({ tab, isActive, onClick }: { tab: Tab; isActive: boolean; onClick: () => void }) => (
    <button
      type="button"
      onClick={onClick}
      className={`
        flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors
        ${
          isActive
            ? "bg-sa-surface-hover text-white"
            : "text-sa-text-dim hover:text-sa-text hover:bg-sa-surface-hover"
        }
      `}
    >
      {tab.label}
      {tab.count !== undefined && tab.count > 0 && (
        <span
          className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] ${
            isActive ? "bg-sa-text-dim" : "bg-sa-surface-hover"
          }`}
        >
          {tab.count}
        </span>
      )}
    </button>
  ),
);

TabButton.displayName = "TabButton";

/**
 * Inspector Bottom Sheet
 *
 * Mobile-friendly slide-up panel with tabbed inspector views.
 */
export const InspectorBottomSheet = memo(
  ({ schema, isOpen, onClose, className = "" }: InspectorBottomSheetProps) => {
    const [activeTab, setActiveTab] = useState<TabId>("algebra");

    const expressionCount = Object.keys(schema.contract_algebra?.expressions || {}).length;
    const observableCount = Object.keys(schema.observables || {}).length;

    const tabs: Tab[] = [
      { id: "algebra", label: "Algebra", count: expressionCount },
      { id: "observables", label: "Observables", count: observableCount },
      { id: "status", label: "Status" },
    ];

    const handleTabChange = useCallback((tabId: TabId) => {
      setActiveTab(tabId);
    }, []);

    if (!isOpen) {
      return null;
    }

    return (
      <div className={`absolute inset-0 z-50 flex flex-col justify-end ${className}`}>
        {/* Backdrop - clickable to close */}
        <button
          type="button"
          className="absolute inset-0 bg-black/40 cursor-default"
          onClick={onClose}
          aria-label="Close inspector"
        />

        {/* Sheet */}
        <div
          className="relative bg-sa-surface rounded-t-xl border-t border-sa-border
            max-h-[70vh] flex flex-col animate-slide-up"
          role="dialog"
          aria-modal="true"
          aria-label="Inspector panel"
        >
          {/* Header */}
          <div className="flex-shrink-0">
            <DragHandle />

            <div className="flex items-center justify-between px-4 pb-2">
              <h2 className="text-sm font-semibold text-sa-text">Inspector</h2>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 text-sa-text-dim hover:text-sa-text hover:bg-sa-surface-hover
                  rounded-lg transition-colors"
                aria-label="Close inspector"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-3 pb-2">
              {tabs.map((tab) => (
                <TabButton
                  key={tab.id}
                  tab={tab}
                  isActive={activeTab === tab.id}
                  onClick={() => handleTabChange(tab.id)}
                />
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {activeTab === "algebra" && (
              <div className="h-full overflow-y-auto">
                <AlgebraInspectorPanel contractAlgebra={schema.contract_algebra} compact />
              </div>
            )}

            {activeTab === "observables" && (
              <div className="h-full overflow-y-auto">
                <ObservablesInspectorPanel observables={schema.observables} compact />
              </div>
            )}

            {activeTab === "status" && (
              <div className="h-full overflow-y-auto p-3">
                <SchemaStatusHeader schema={schema} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

InspectorBottomSheet.displayName = "InspectorBottomSheet";

/**
 * Bottom Sheet Toggle Button
 *
 * Floating action button to open the inspector bottom sheet.
 */
export const InspectorToggleButton = memo(
  ({
    onClick,
    hasContent,
    className = "",
  }: {
    onClick: () => void;
    hasContent: boolean;
    className?: string;
  }) => {
    if (!hasContent) {
      return null;
    }

    return (
      <button
        type="button"
        onClick={onClick}
        className={`
          absolute bottom-4 right-4 z-10
          p-3 bg-sa-surface/90 hover:bg-sa-surface-hover
          text-sa-text rounded-full shadow-lg
          transition-colors
          ${className}
        `}
        title="Open inspector"
        aria-label="Open inspector panel"
      >
        <svg
          className="w-5 h-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
          <rect x="9" y="3" width="6" height="4" rx="1" />
          <path d="M9 12h6M9 16h6" />
        </svg>
      </button>
    );
  },
);

InspectorToggleButton.displayName = "InspectorToggleButton";
